import { useParams, Link, useNavigate } from 'react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../components/ui/alert-dialog';
import { ArrowLeft, Archive, ArchiveRestore, Code } from 'lucide-react';
import { formatDateTime } from '../lib/utils';
import { toast } from 'sonner';

export function ServiceDetailsPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: service, isLoading } = useQuery({
    queryKey: ['service', id],
    queryFn: () => api.get('/api/services/get', { params: { service_instance_id: id } }).then((r) => r.data),
    enabled: !!id,
  });

  const archiveMutation = useMutation({
    mutationFn: () => api.post('/api/services/archive', { service_instance_id: id }),
    onSuccess: () => {
      toast.success('Service archived');
      queryClient.invalidateQueries({ queryKey: ['services'] });
      navigate('/services');
    },
    onError: () => toast.error('Failed to archive service'),
  });

  const restoreMutation = useMutation({
    mutationFn: () => api.post('/api/services/archives/restore', { service_instance_id: id }),
    onSuccess: () => {
      toast.success('Service restored');
      queryClient.invalidateQueries({ queryKey: ['services'] });
      queryClient.invalidateQueries({ queryKey: ['service', id] });
    },
    onError: () => toast.error('Failed to restore service'),
  });

  const { data: codeData } = useQuery({
    queryKey: ['service-code', id],
    queryFn: () => api.post('/api/services/generateCode', { service_instance_id: id }).then((r) => r.data),
    enabled: !!id,
  });

  if (isLoading) {
    return <div className="text-center py-12 text-gray-500">Loading service details...</div>;
  }

  const s = service?.query?.[0] ?? service;

  if (!s) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500 mb-4">Service not found</p>
        <Link to="/services"><Button variant="outline">Back to Services</Button></Link>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center gap-3 mb-6">
        <Link to="/services">
          <Button variant="ghost" size="icon"><ArrowLeft className="h-5 w-5" /></Button>
        </Link>
        <div className="flex-1">
          <h1 className="text-2xl font-bold">{s.service_instance_name}</h1>
          <p className="text-gray-500">{s.source_app_name} → {s.target_app_name}</p>
        </div>
        <Badge variant={s.is_active ? 'success' : 'secondary'}>
          {s.is_active ? 'Active' : s.is_archived ? 'Archived' : 'Inactive'}
        </Badge>
      </div>

      <Card className="mb-6">
        <CardHeader><CardTitle className="text-lg">Service Information</CardTitle></CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div><span className="text-gray-500">Name:</span> <span className="font-medium ml-2">{s.service_instance_name}</span></div>
            <div><span className="text-gray-500">Definition:</span> <span className="font-medium ml-2">{s.service_definition_name || '—'}</span></div>
            <div><span className="text-gray-500">Source App:</span> <span className="font-medium ml-2">{s.source_app_name || '—'}</span></div>
            <div><span className="text-gray-500">Target App:</span> <span className="font-medium ml-2">{s.target_app_name || '—'}</span></div>
            <div><span className="text-gray-500">Created:</span> <span className="font-medium ml-2">{formatDateTime(s.created)}</span></div>
            <div><span className="text-gray-500">Modified:</span> <span className="font-medium ml-2">{formatDateTime(s.modified)}</span></div>
            {s.code && <div><span className="text-gray-500">Service Code:</span> <code className="ml-2 bg-gray-100 px-2 py-0.5 rounded text-xs">{s.code}</code></div>}
          </div>
        </CardContent>
      </Card>

      {/* Integration Code */}
      {codeData && (
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Code className="h-5 w-5" /> Integration Code
            </CardTitle>
          </CardHeader>
          <CardContent>
            <pre className="bg-gray-50 border rounded-md p-4 text-xs overflow-x-auto">
              {typeof codeData === 'string' ? codeData : JSON.stringify(codeData, null, 2)}
            </pre>
          </CardContent>
        </Card>
      )}

      <Separator className="my-6" />

      <div>
        <h2 className="text-lg font-semibold mb-3">Actions</h2>
        <div className="flex gap-3">
          {s.is_archived ? (
            <Button variant="outline" onClick={() => restoreMutation.mutate()} disabled={restoreMutation.isPending}>
              <ArchiveRestore className="h-4 w-4 mr-2" />
              {restoreMutation.isPending ? 'Restoring...' : 'Restore'}
            </Button>
          ) : (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="outline">
                  <Archive className="h-4 w-4 mr-2" /> Archive
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Archive {s.service_instance_name}?</AlertDialogTitle>
                  <AlertDialogDescription>
                    This will archive the service. You can restore it later from the archived services list.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={() => archiveMutation.mutate()} disabled={archiveMutation.isPending}>
                    {archiveMutation.isPending ? 'Archiving...' : 'Archive'}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
        </div>
      </div>
    </div>
  );
}